'use client';

import { Suspense } from 'react';
import cx from 'clsx';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { Badge } from '@kcd/design-system';

import { useSuspenseQuery } from '~/api/hooks';
import { CART_QUERY_KEY } from '~/api/query-keys';
import useAddCartTooltipStore from '~/store/global/useAddCartTooltipStore';
import { changedByFontScale } from '~/utils/fontScale';

import CartIcon from '~/assets/icons/gnb/ic_cart_line_24x24.svg';

const tooltipAnimation = {
  animation: 'tooltipHide 3.6s ease forwards',
};

const tooltipKeyframes = `
  @keyframes tooltipHide {
    0% { opacity: 1; }
    50% { opacity: 1; }
    80% { top: calc(100% + 10px); opacity: 0; visibility: hidden; }
    100% { top: -9999px; }
  }
`;

type TooltipWrapperProps = {
  rightPosition: string;
  arrowRightPosition: string;
  children: React.ReactNode;
};

function TooltipWrapper({
  rightPosition,
  arrowRightPosition,
  children,
}: TooltipWrapperProps) {
  return (
    <>
      <style>{tooltipKeyframes}</style>
      <div
        className={cx(
          'absolute flex min-w-max py-12 px-16',
          'typo-caption100-regular text-inverse rounded-16 z-[9999]',
          rightPosition,
        )}
        style={{
          background: 'rgba(34, 43, 62, 0.95)',
          top: 'calc(100% + 10px)',
          ...tooltipAnimation,
        }}
      >
        <div
          className="absolute"
          style={{
            width: 0,
            height: 0,
            bottom: '100%',
            right: arrowRightPosition,
            borderStyle: 'solid',
            borderWidth: '0 7px 7px',
            borderColor: 'transparent transparent var(--colors-gray-1200)',
          }}
        />
        {children}
      </div>
    </>
  );
}

function TooltipContent() {
  return (
    <>
      <div className="text-gray-000 typo-title100-semibold">
        장바구니에서
        <br />
        구매를 완료해주세요.
      </div>
      <div
        className={cx(
          'relative block flex-shrink-0 typo-paragraph300-semibold',
          'ml-[75px] bg-gray-000 text-gray-1400',
          'border border-solid border-03 rounded-12',
        )}
        style={{ padding: '7px 12px' }}
        role="presentation"
      >
        주문하기
      </div>
    </>
  );
}

function AddCartTooltip() {
  return (
    <Link href="/cart">
      <TooltipWrapper rightPosition="-right-[42px]" arrowRightPosition="46px">
        <TooltipContent />
      </TooltipWrapper>
    </Link>
  );
}

function AddCartTooltipMypage() {
  return (
    <Link href="/cart">
      <TooltipWrapper rightPosition="-right-[12px]" arrowRightPosition="16px">
        <TooltipContent />
      </TooltipWrapper>
    </Link>
  );
}

function ShoppingCartLinkContent({ count }: { count: number }) {
  const pathname = usePathname();
  const { isVisible } = useAddCartTooltipStore();

  return (
    <Link href="/cart">
      <div className="relative size-[36px] p-6 text-transparent">
        <CartIcon />
        {count > 0 && (
          <Badge
            className={cx([
              'absolute top-4 left-[18px]',
              changedByFontScale() && '-top-[0.75em] -right-[0.75em]',
            ])}
            type="number"
            text={count}
          />
        )}
        {isVisible &&
          (pathname === '/mypage' ? (
            <AddCartTooltipMypage />
          ) : (
            <AddCartTooltip />
          ))}
      </div>
    </Link>
  );
}

function ShoppingCartLinkContainer() {
  const { data: count } = useSuspenseQuery(CART_QUERY_KEY.count());

  return <ShoppingCartLinkContent count={count || 0} />;
}

export function ShoppingCartLink() {
  return (
    <Suspense fallback={<ShoppingCartLinkContent count={0} />}>
      <ShoppingCartLinkContainer />
    </Suspense>
  );
}
